import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Edit, 
  Trash2, 
  Package, 
  DollarSign, 
  BarChart3, 
  Tag, 
  Hash,
  Calendar,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Image as ImageIcon
} from 'lucide-react';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { dataHelpers } from '../../lib/supabase';
import toast from 'react-hot-toast';

const ProductDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { company } = useAuth();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (company?.id && id) {
      loadProduct();
    }
  }, [company, id]);

  const loadProduct = async () => {
    try {
      setLoading(true);
      console.log('Loading product with ID:', id, 'for company:', company.id);
      
      const foundProduct = await dataHelpers.getProductById(company.id, id);
      
      if (!foundProduct) {
        console.log('Product not found with ID:', id);
        toast.error('Produto não encontrado');
        navigate('/dashboard/products');
        return;
      }
      
      console.log('Product loaded successfully:', foundProduct);
      setProduct(foundProduct);
    } catch (error) {
      console.error('Error loading product:', error);
      toast.error('Erro ao carregar produto');
      navigate('/dashboard/products');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Tem certeza que deseja excluir o produto "${product.name}"?`)) {
      return;
    }

    try {
      await dataHelpers.deleteProduct(product.id);
      toast.success('Produto excluído com sucesso');
      navigate('/dashboard/products');
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Erro ao excluir produto');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Não informado';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getMargin = () => {
    if (!product.price || !product.cost) return 0;
    return ((product.price - product.cost) / product.price * 100).toFixed(1);
  };

  const getStockStatus = () => {
    if (!product.stock_quantity || !product.min_stock) return 'normal';
    
    if (product.stock_quantity <= 0) return 'out';
    if (product.stock_quantity <= product.min_stock) return 'low';
    return 'normal';
  };

  const getStockStatusInfo = () => {
    const status = getStockStatus();
    
    switch (status) {
      case 'out':
        return {
          color: 'text-red-600',
          bgColor: 'bg-red-100',
          icon: TrendingDown,
          text: 'Sem estoque'
        };
      case 'low':
        return {
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-100',
          icon: AlertTriangle,
          text: 'Estoque baixo'
        };
      default:
        return {
          color: 'text-green-600',
          bgColor: 'bg-green-100',
          icon: CheckCircle,
          text: 'Estoque normal'
        };
    }
  };

  if (loading) {
    return (
      
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="large" />
        </div>
      
    );
  }

  if (!product) {
    return (
      
        <div className="text-center py-12">
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Produto não encontrado</h2>
          <p className="text-gray-600 mb-6">O produto que você está procurando não existe ou foi removido.</p>
          <Link to="/dashboard/products">
            <Button>Voltar para Produtos</Button>
          </Link>
        </div>
      
    );
  }

  const stockStatus = getStockStatusInfo();
  const StatusIcon = stockStatus.icon;

  return (
    
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard/products')}
              className="flex items-center"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{product.name}</h1>
              <p className="text-gray-600">
                Detalhes do produto • Código: {product.barcode || 'Não informado'}
              </p>
            </div>
          </div>
          
          <div className="flex space-x-3">
            <Link to={`/dashboard/products/${product.id}/edit`}>
              <Button className="bg-blue-600 hover:bg-blue-700">
                <Edit className="w-4 h-4 mr-2" />
                Editar
              </Button>
            </Link>
            <Button
              variant="outline"
              onClick={handleDelete}
              className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Excluir
            </Button>
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Product Image and Basic Info */}
            <Card className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Image */}
                <div className="flex justify-center">
                  {product.image_url ? (
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full max-w-xs h-64 object-cover rounded-lg border border-gray-200"
                    />
                  ) : (
                    <div className="w-full max-w-xs h-64 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center">
                      <div className="text-center">
                        <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">Sem imagem</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Basic Info */}
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Informações Básicas</h3>
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <Package className="w-4 h-4 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm text-gray-600">Nome</p>
                          <p className="font-medium">{product.name}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center">
                        <Tag className="w-4 h-4 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm text-gray-600">Categoria</p>
                          <p className="font-medium">{product.category}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center">
                        <Hash className="w-4 h-4 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm text-gray-600">Código de Barras</p>
                          <p className="font-medium">{product.barcode || 'Não informado'}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm text-gray-600">Cadastrado em</p>
                          <p className="font-medium">{formatDate(product.created_at)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Description */}
              {product.description && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h4 className="font-medium text-gray-900 mb-2">Descrição</h4>
                  <p className="text-gray-600">{product.description}</p>
                </div>
              )}
            </Card>

            {/* Stock Information */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Controle de Estoque</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${stockStatus.bgColor} ${stockStatus.color} mb-2`}>
                    <StatusIcon className="w-4 h-4 mr-1" />
                    {stockStatus.text}
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{product.stock_quantity}</p>
                  <p className="text-sm text-gray-600">Quantidade atual</p>
                </div>
                
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600">{product.min_stock}</div>
                  <p className="text-sm text-gray-600">Estoque mínimo</p>
                </div>
                
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{product.unit}</div>
                  <p className="text-sm text-gray-600">Unidade de medida</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Financial Info */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Informações Financeiras</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                  <div>
                    <p className="text-sm text-green-700">Preço de Venda</p>
                    <p className="text-xl font-bold text-green-800">{formatCurrency(product.price)}</p>
                  </div>
                  <DollarSign className="w-8 h-8 text-green-600" />
                </div>
                
                {product.cost && (
                  <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                    <div>
                      <p className="text-sm text-blue-700">Custo</p>
                      <p className="text-xl font-bold text-blue-800">{formatCurrency(product.cost)}</p>
                    </div>
                    <BarChart3 className="w-8 h-8 text-blue-600" />
                  </div>
                )}
                
                {product.cost && (
                  <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                    <div>
                      <p className="text-sm text-purple-700">Margem de Lucro</p>
                      <p className="text-xl font-bold text-purple-800">{getMargin()}%</p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-purple-600" />
                  </div>
                )}
              </div>
            </Card>

            {/* Status */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Status do Produto</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Ativo</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    product.active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {product.active ? 'Sim' : 'Não'}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Última atualização</span>
                  <span className="text-sm text-gray-900">{formatDate(product.updated_at)}</span>
                </div>
              </div>
            </Card>

            {/* Quick Actions */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Ações Rápidas</h3>
              <div className="space-y-3">
                <Link to={`/dashboard/products/${product.id}/edit`} className="block">
                  <Button variant="outline" className="w-full justify-start">
                    <Edit className="w-4 h-4 mr-2" />
                    Editar Produto
                  </Button>
                </Link>
                
                <Button variant="outline" className="w-full justify-start" disabled>
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Ver Relatório de Vendas
                </Button>
                
                <Button variant="outline" className="w-full justify-start" disabled>
                  <Package className="w-4 h-4 mr-2" />
                  Ajustar Estoque
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </div>
    
  );
};

export default ProductDetailsPage;
